import React, { useCallback, useEffect, useState } from "react";
import { http, inr, requestErrorMessage } from "@/lib/erp";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Truck, Search, ExternalLink, FileCheck2, Download } from "lucide-react";
import { toast } from "sonner";

const EWAY_PORTAL = "https://ewaybillgst.gov.in";

function downloadJSON(name, obj) {
  const blob = new Blob([JSON.stringify(obj, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = name; a.click();
  URL.revokeObjectURL(url);
}

export default function EwayBills() {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState("");
  const [loadError, setLoadError] = useState("");
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async (query = search) => {
    setLoading(true);
    try {
      const { data } = await http.get("/invoices", { params: { search: query } });
      setRows(data);
      setLoaded(true);
      setLoadError("");
    } catch (e) {
      setLoadError(requestErrorMessage(e, "load invoices"));
    } finally {
      setLoading(false);
    }
  }, [search]);
  useEffect(() => { const t = setTimeout(() => load(search), 250); return () => clearTimeout(t); }, [load, search]);

  const genEway = async (id) => {
    setBusy(id);
    try { const { data } = await http.post(`/invoices/${id}/eway`); toast.success(`E-Way Bill ${data.eway_bill} generated (mock)`); await load(); }
    catch (e) { toast.error(requestErrorMessage(e, "generate e-way bill")); }
    finally { setBusy(""); }
  };

  const exportEwayJson = async (inv) => {
    try {
      const { data } = await http.get(`/invoices/${inv.id}`);
      const c = data.company || {};
      const payload = {
        version: "1.0.3",
        billLists: [{
          userGstin: c.gstin || "",
          supplyType: "O", subSupplyType: "1", docType: "INV",
          docNo: data.invoice_no, docDate: (data.invoice_date || "").split("-").reverse().join("/"),
          fromGstin: c.gstin || "", fromTrdName: c.name || "", fromStateCode: Number(c.state_code || 36),
          toGstin: data.customer_gstin || "URP", toTrdName: data.customer_name,
          toStateCode: Number(data.place_of_supply_code || 36),
          totalValue: data.subtotal, cgstValue: data.cgst, sgstValue: data.sgst, igstValue: data.igst,
          totInvValue: data.grand_total,
          itemList: (data.items || []).map((it, i) => ({
            productName: it.name, hsnCode: it.hsn, quantity: it.qty, qtyUnit: it.unit || "PCS",
            taxableAmount: it.taxable, cgstRate: data.intra_state ? it.gst_rate / 2 : 0,
            sgstRate: data.intra_state ? it.gst_rate / 2 : 0, igstRate: data.intra_state ? 0 : it.gst_rate,
          })),
          transMode: "1", transDistance: "0",
        }],
      };
      downloadJSON(`EWB_${data.invoice_no}.json`, payload);
      toast.success("E-Way JSON downloaded");
    } catch (e) { toast.error(requestErrorMessage(e, "export e-way bill JSON")); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">E-Way Bills</h1>
          <p className="text-sm text-muted-foreground">Generate & track e-way bills · file on the government portal</p>
        </div>
        <a href={EWAY_PORTAL} target="_blank" rel="noreferrer" data-testid="eway-portal-link">
          <Button data-testid="open-eway-portal"><ExternalLink className="h-4 w-4 mr-1" /> Open E-Way Bill Portal</Button>
        </a>
      </div>

      <Card className="p-4 bg-brand-50/50 border-brand-200">
        <div className="flex items-start gap-3 text-sm">
          <Truck className="h-5 w-5 text-brand-600 shrink-0 mt-0.5" />
          <div>
            An <span className="font-semibold">e-Way Bill</span> is required for the movement of goods worth over ₹50,000. Generate the bill number here, then log in to the
            {" "}<a href={EWAY_PORTAL} target="_blank" rel="noreferrer" className="text-brand-700 underline font-medium">official e-Way Bill portal</a>{" "}
            to file Part-A / Part-B and print the final EWB.
          </div>
        </div>
      </Card>

      <Card className="p-0 overflow-hidden">
        <div className="p-4 border-b">
          <div className="relative max-w-sm">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder="Search invoice / customer…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="eway-search" />
          </div>
        </div>
        {loadError && <div role="alert" className="flex items-center justify-between gap-3 border-b border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
          <span>{loadError}</span><Button size="sm" variant="outline" onClick={() => load()}>Retry</Button>
        </div>}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50"><tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
              <th className="px-4 py-3 font-medium">Invoice</th><th className="px-4 py-3 font-medium">Date</th><th className="px-4 py-3 font-medium">Customer</th>
              <th className="px-4 py-3 font-medium text-right">Value</th><th className="px-4 py-3 font-medium">E-Way Bill No</th><th className="px-4 py-3 font-medium text-right">Actions</th>
            </tr></thead>
            <tbody>
              {rows.map((i) => (
                <tr key={i.id} className="border-t hover:bg-slate-50" data-testid={`eway-row-${i.id}`}>
                  <td className="px-4 py-3 font-mono font-medium">{i.invoice_no}</td>
                  <td className="px-4 py-3 text-muted-foreground">{i.invoice_date}</td>
                  <td className="px-4 py-3">{i.customer_name}</td>
                  <td className="px-4 py-3 text-right font-mono">{inr(i.grand_total)}</td>
                  <td className="px-4 py-3">{i.eway_bill
                    ? <span className="font-mono text-xs bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded" data-testid={`eway-no-${i.id}`}>{i.eway_bill}</span>
                    : <span className="text-xs text-muted-foreground">Not generated</span>}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <Button size="sm" variant="outline" disabled={busy === i.id} onClick={() => genEway(i.id)} data-testid={`gen-eway-${i.id}`}>
                        <FileCheck2 className="h-3.5 w-3.5 mr-1" /> {i.eway_bill ? "Regenerate" : "Generate"}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => exportEwayJson(i)} data-testid={`eway-json-${i.id}`}>
                        <Download className="h-3.5 w-3.5 mr-1" /> JSON
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
              {loading && rows.length === 0 && !loadError && <tr><td colSpan={6} className="px-4 py-12 text-center text-muted-foreground">Loading invoices…</td></tr>}
              {!loading && !loadError && loaded && rows.length === 0 && <tr><td colSpan={6} className="px-4 py-12 text-center text-muted-foreground"><Truck className="h-8 w-8 mx-auto mb-2 opacity-40" />No invoices</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
